import {
  IdentificationKind,
  type IdentificationSide,
  MY_NUMBER_BACK_NOTICE,
  requiredIdentificationSides,
  textIdentificationKind,
  textIdentificationSide,
} from '@maru44/huntre-utils/src/models/deviceBuyback'
import { Alert, Box, Button, MenuItem, Paper, Select, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'

type Props = {
  onSubmit: (kind: IdentificationKind, files: Partial<Record<IdentificationSide, File>>) => Promise<void>
}

/** 差し戻し (needsFix) のときだけ出す、本人確認書類の差し替えフォーム */
export const ResubmitForm = ({ onSubmit }: Props) => {
  const [kind, setKind] = useState<IdentificationKind>(IdentificationKind.DriversLicense)
  const [files, setFiles] = useState<Partial<Record<IdentificationSide, File>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 要求する面は書類で変わる。マイナンバーカードは裏面を出させない
  const requiredSides = requiredIdentificationSides(kind)
  const hasAllSides = requiredSides.every((side) => !!files[side])

  const handleSubmit = useCallback(async () => {
    if (!hasAllSides) {
      toast.error('必要な面の画像を選択してください')
      return
    }
    setIsSubmitting(true)
    try {
      await onSubmit(kind, files)
      toast.success('再提出しました')
      setFiles({})
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '再提出に失敗しました')
    } finally {
      setIsSubmitting(false)
    }
  }, [files, hasAllSides, kind, onSubmit])

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">本人確認書類の再提出</Typography>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            書類の種別
          </Typography>
          <Select
            fullWidth
            size="small"
            id="resubmitIdentificationKind"
            name="resubmitIdentificationKind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as IdentificationKind)
              setFiles({})
            }}
          >
            {Object.values(IdentificationKind).map((v) => (
              <MenuItem key={v} value={v}>
                {textIdentificationKind(v)}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {requiredSides.map((side) => (
          <Box key={side}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {textIdentificationSide(kind, side)}
            </Typography>
            <Box>
              <input
                type="file"
                accept="image/*"
                id={`resubmit_${side}`}
                name={`resubmit_${side}`}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  setFiles((p) => ({ ...p, [side]: f ?? undefined }))
                }}
              />
            </Box>
          </Box>
        ))}

        {kind === IdentificationKind.MyNumberCard && <Alert severity="warning">{MY_NUMBER_BACK_NOTICE}</Alert>}

        <Box>
          <Button variant="contained" disabled={isSubmitting || !hasAllSides} onClick={handleSubmit}>
            再提出する
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}
